import {
    BlobServiceClient,
    BlockBlobParallelUploadOptions,
    AnonymousCredential,
    BlobUploadCommonResponse,
} from "@azure/storage-blob";
import { IFileMetadata, IUploadProgress } from "@/models";
import { NotificationService } from ".";

export class AzureBlobService {
    public uploadProgress: IUploadProgress[];

    private readonly blobServiceClient: BlobServiceClient;
    private extensionRegexp = new RegExp("(?:\.([^.]+))?$");

    constructor(private containerName: string, private blobUri: string, private sasToken: string) {
        this.containerName = containerName;
        this.blobUri = blobUri;
        this.sasToken = sasToken;
        this.uploadProgress = [];

        this.blobServiceClient = new BlobServiceClient(this.blobUriWithSas, new AnonymousCredential());
    }

    public async uploadFile(
        file: File,
        blobKey: string,
        uniqueName: string,
        metadata?: IFileMetadata)
        : Promise<string> {
        if (file.size < 2e9) {
            const extension: string = this.extensionRegexp.exec(file.name)[1];

            const uniquePath = `${blobKey}/${uniqueName}`;
            const containerClient = this.blobServiceClient.getContainerClient(this.containerName);
            const blockBlobClient = containerClient.getBlockBlobClient(uniquePath);
            const progress = {
                fileName: uniqueName,
                fileSize: file.size,
                uploadProgress: 0,
            };

            const progressIndex = this.uploadProgress.push(progress) - 1;
            const uploadOptions: BlockBlobParallelUploadOptions = {
                metadata: this.encodeObject(metadata),
                blobHTTPHeaders: {
                    blobContentDisposition: this.buildContentDispositionHeader(file.name),
                    blobContentType: this.defineMimeType(extension),
                },
                onProgress: state => {
                    const newArray = [...this.uploadProgress];
                    const progressValue = {
                            fileName: uniqueName,
                            fileSize: file.size,
                            uploadProgress: state.loadedBytes,
                    };

                    newArray[progressIndex] = progressValue;
                    this.uploadProgress = newArray;
                  },
            };

            const response: BlobUploadCommonResponse = await blockBlobClient.uploadBrowserData(file, uploadOptions);
            if (!response.errorCode) {
                const message: string = `File '${file.name}' uploaded`;
                NotificationService.showSuccess(message);
            } else {
                const message: string = `Can not upload '${file.name}'`;
                NotificationService.showError(message);
            }

            return uniqueName;
        } else {
            NotificationService.showError(`Selected file '${file.name}}' size must be within 2Go`);
            return null;
        }
    }

    private get blobUriWithSas(): string {
        return `${this.blobUri}?${this.sasToken}`;
    }

    private encodeObject(obj: IFileMetadata): IFileMetadata {
        if (!!obj) {
            for (const key of Object.keys(obj)) {
                obj[key] = encodeURIComponent(obj[key]);
            }
        }

        return obj;
    }

    private buildContentDispositionHeader(fileName: string): string {
        return `filename="${encodeURIComponent(fileName)}"`;
    }


    private defineMimeType(extension: string): string {
        switch (extension?.toLowerCase()) {
            case "pdf": {
                return "application/pdf";
            }
            default:
                return "";
        }
    }
}
